import { useRouter } from '@tanstack/react-router';
import { backButton } from '@telegram-apps/sdk-react';
import { useCallback, useEffect } from 'react';

import { safeTma } from '@common/utils/safeTma';

export interface UseTelegramBackButtonOptions {
  to?: string | 'back';
  onClick?: () => void;
  enabled?: boolean;
}

export const useTelegramBackButton = ({
  to,
  onClick,
  enabled = true,
}: UseTelegramBackButtonOptions) => {
  const router = useRouter();

  const handleClick = useCallback(() => {
    if (to === 'back') {
      router.history.back();
    } else if (to) {
      router.history.push(to);
    }

    onClick?.();
  }, [onClick, router, to]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Each call is guarded INDEPENDENTLY, not as one block: if `show` threw
    // inside a shared try, `onClick` would never register, and the cleanup's
    // `offClick(handleClick)` would then unregister a handler that never
    // existed. Independent guards keep registration symmetric.
    //
    // `onClick` only REGISTERS the handler — `handleClick` itself is invoked
    // later by the SDK's emitter, outside this try, so the guard structurally
    // cannot swallow router navigation errors.
    safeTma('backButton.show', () => backButton.show.ifAvailable());
    safeTma('backButton.onClick', () =>
      backButton.onClick.ifAvailable(handleClick),
    );

    return () => {
      // Cleanup is guarded too. `hide` is inert only by accident today (the
      // SDK's postEvent throws before it records the button as visible, so the
      // `is_visible` change check short-circuits) — the moment the transport
      // works once and then dies mid-session, `hide` throws during unmount,
      // which React handles far worse than a throw during render.
      safeTma('backButton.offClick', () =>
        backButton.offClick.ifAvailable(handleClick),
      );
      safeTma('backButton.hide', () => backButton.hide.ifAvailable());
    };
  }, [enabled, handleClick]);
};
